import { useState } from "react";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Waitlist / "get in touch" form. Submits to the public `submitWaitlist` action
 * (posts to our Slack waitlist channel). No email address is shown anywhere —
 * this form IS the contact method. `compact` hides the note field for inline use.
 */
export function WaitlistForm({
  source,
  compact = false,
}: {
  source: string;
  compact?: boolean;
}) {
  const submit = useAction(api.waitlist.submitWaitlist);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const clean = email.trim();
    if (!EMAIL_RE.test(clean)) {
      setError("Please enter a valid email address.");
      return;
    }
    setStatus("sending");
    setError(null);
    try {
      await submit({
        email: clean,
        note: note.trim() || undefined,
        source,
      });
      setStatus("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn’t submit — please try again.");
      setStatus("idle");
    }
  }

  if (status === "done") {
    return (
      <p className="waitlist-done">
        ✓ You’re on the list — we’ll be in touch.
      </p>
    );
  }

  return (
    <form className={`form waitlist-form${compact ? " waitlist-compact" : ""}`} onSubmit={onSubmit} noValidate>
      <input
        className="input"
        type="email"
        required
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        aria-label="Email address"
      />
      {!compact && (
        <textarea
          className="input"
          rows={3}
          placeholder="Anything you’d like us to know? (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Message (optional)"
        />
      )}
      {error && <p className="form-error">{error}</p>}
      <button className="btn btn-primary" type="submit" disabled={status === "sending"}>
        {status === "sending" ? "Sending…" : "Join the waitlist"}
      </button>
    </form>
  );
}
